import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const pillars = [
  {
    icon: 'ri-seedling-line',
    title: 'Developed, Not Just Taught',
    desc: 'Our approach goes beyond skills. We build confidence, discipline, and purpose — creating an atmosphere where individuals grow at their own pace and feel truly understood.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Structure With Safety',
    desc: 'We provide a safe, structured environment where every creative feels supported. Especially designed with neurodivergent individuals in mind — because everyone deserves patience and care.',
  },
  {
    icon: 'ri-star-smile-line',
    title: 'Potential Fully Realized',
    desc: 'We believe every person has something valuable within them. With the right guidance and environment, that potential can be fully realized and given the opportunity to thrive.',
  },
  {
    icon: 'ri-community-line',
    title: 'Community & Connection',
    desc: 'Creative Institute is more than a school — it\'s a space where individuals are empowered, celebrated, and connected with a community that lifts them higher.',
  },
];

export default function WhySection() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: quoteRef, isVisible: quoteVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-28 bg-gradient-to-br from-[#FFFBEB]/60 via-white to-[#EEF2FF]/40 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#EEF2FF] to-transparent rounded-full opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#FEF9C3] to-transparent rounded-full opacity-50 pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        {/* Section Header */}
        <div ref={headRef} className={`scroll-hidden ${headVisible ? 'scroll-visible' : ''} text-center mb-20`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-yellow/30 mb-5">
            Why Creative Institute
          </span>
          <h2 className="font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark leading-tight mb-6">
            More Than a Place to Learn —<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">A Space to Become</span>
          </h2>
          <p className="font-body text-gray-500 text-lg max-w-3xl mx-auto leading-relaxed">
            Creative Institute was built with intention — to create more than just a place to learn, but a space where individuals are developed, supported, and empowered through creativity.
          </p>
        </div>

        {/* Main Content Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Image */}
          <div className={`scroll-hidden slide-left ${headVisible ? 'scroll-visible' : ''}`}>
            <div className="relative rounded-3xl overflow-hidden h-[480px]">
              <img
                src="https://storage.readdy-site.link/project_files/979cb23d-2376-41a4-bad4-0dbbe3d67aab/3bcaa9e9-8714-4883-bad3-a68177471aaa__DSC2959.jpeg?v=34ff5ce349e5abe58e21404286800952"
                alt="Creative Institute learning environment"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/40 via-transparent to-transparent" />
              {/* Floating card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-5">
                <p className="font-heading text-brand-blue-dark text-lg font-bold italic leading-snug">
                  &ldquo;Where Every Creative Mind Matters.&rdquo;
                </p>
                <p className="font-body text-gray-500 text-xs mt-1">— The Creative Institute Promise</p>
              </div>
            </div>
          </div>

          {/* Right: Text */}
          <div className={`scroll-hidden slide-right ${headVisible ? 'scroll-visible' : ''} space-y-6`}>
            <p className="font-body text-gray-600 text-base leading-loose">
              Whether you are beginning your journey or refining your craft, Creative Institute is a place where <strong className="text-brand-blue-dark">discipline meets creativity</strong> — and every individual is seen, supported, and empowered.
            </p>
            <p className="font-body text-gray-600 text-base leading-loose">
              Our approach goes beyond teaching skills. We focus on building <strong className="text-brand-blue-dark">confidence</strong>, <strong className="text-brand-blue-dark">discipline</strong>, and <strong className="text-brand-blue-dark">purpose</strong> — creating an atmosphere where individuals grow at their own pace, feel truly understood, and develop into who they are meant to be.
            </p>
            <p className="font-body text-gray-600 text-base leading-loose">
              We believe creativity is not one-size-fits-all. Our environments are intentionally designed for individuals with autism, ADHD, and diverse learning styles to thrive and express themselves freely.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EEF2FF] text-brand-blue-dark font-body text-xs font-semibold">
                <i className="ri-check-line text-brand-purple" /> 5+ Years of Experience
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FEF9C3] text-brand-yellow-dark font-body text-xs font-semibold">
                <i className="ri-check-line" /> Inclusive Environment
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5F3FF] text-brand-purple font-body text-xs font-semibold">
                <i className="ri-check-line" /> Neurodivergent Friendly
              </span>
            </div>
          </div>
        </div>

        {/* Four Pillars Grid */}
        <div ref={gridRef} className={`scroll-hidden ${gridVisible ? 'scroll-visible' : ''} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20`}>
          {pillars.map((pillar, i) => (
            <div
              key={pillar.title}
              className={`scroll-hidden-blur ${gridVisible ? 'scroll-visible' : ''} p-7 rounded-2xl bg-gradient-to-br from-white to-[#F8F7FF] border border-[#EDE9FE] card-lift`}
              style={{ transitionDelay: `${i * 130}ms` }}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white mb-5 wiggle-hover`}>
                <i className={`${pillar.icon} text-xl`} />
              </div>
              <h4 className="font-heading text-brand-blue-dark text-lg font-bold mb-3">{pillar.title}</h4>
              <p className="font-body text-gray-500 text-sm leading-relaxed">{pillar.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom Quote Banner */}
        <div ref={quoteRef} className={`scroll-hidden ${quoteVisible ? 'scroll-visible' : ''} rounded-3xl bg-gradient-to-r from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] p-12 text-center relative overflow-hidden`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10">
            <i className="ri-double-quotes-l text-5xl text-brand-yellow/40 mb-4 block" />
            <p className="font-heading text-white text-2xl lg:text-3xl font-black leading-snug max-w-3xl mx-auto mb-4">
              Where Every Creative Mind Matters — a place where talent is cultivated, purpose is developed, and every individual is empowered to thrive.
            </p>
            <p className="font-body text-white/60 text-sm uppercase tracking-widest">Creative Institute of Fayetteville, NC</p>
          </div>
        </div>
      </div>
    </section>
  );
}
